import { useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Button,
  Card,
  Collapse,
  Form as AntForm,
  Grid,
  Input,
  InputNumber,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
} from 'antd';
import { History, Save, Trash2 } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { z } from 'zod';
import dayjs from 'dayjs';
import { Form } from '@shared/components/Form/Form';
import { FormField } from '@shared/components/Form/FormField';
import { PageHeader } from '@shared/components/PageHeader/PageHeader';
import { SlugInput } from '@shared/components/SlugInput/SlugInput';
import { RichTextEditor } from '@shared/components/RichTextEditor';
import { SeoPanel, type SeoPanelValue } from '@shared/components/SeoPanel';
import { KeywordsInput } from '@shared/components/KeywordsInput';
import { CustomCodeField } from '@shared/components/CustomCodeField/CustomCodeField';
import { ImageField } from '@shared/components/MediaUpload';
import { StatusBadge } from '@shared/components/StatusBadge';
import { useConfirm } from '@shared/components/ConfirmDialog';
import { pagesApi } from '@api/pages';
import { pageBlocksApi } from '@api/pageBlocks';
import { PageBuilderPanel } from '../components/PageBuilderPanel';
import { NormalizedApiError } from '@api/errors';
import { resolveApiErrorMessage } from '@/i18n/apiError';
import { ErrorCode } from '@shared/constants/errorCodes';
import type { Translator } from '@/i18n/config';
import { recordAuditAction } from '@features/audit/recordAction';
import { useCan } from '@shared/rbac/useCan';
import { useAuth } from '@features/auth/hooks/useAuth';
import { PERMISSIONS } from '@shared/rbac/permissions.const';
import { ROUTES } from '@shared/constants/routes';
import { LanguagePicker } from '@shared/components/LanguagePicker/LanguagePicker';
import { VariantPanel } from '@shared/components/VariantPanel/VariantPanel';
import { useActiveLanguages, pickDefaultLanguage } from '@features/languages/useLanguages';
import { PageVersionsDrawer } from '../components/PageVersionsDrawer';
import {
  PAGE_TYPES,
  toContentStatus,
  usePageStatusLabel,
  usePageTypeLabel,
  type PageStatus,
  type PageType,
  type PageWriteInput,
  type StaticPage,
} from '../types';

const { Text } = Typography;

const SLUG_RULE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;

function makeSchema(t: Translator, requireBody = true) {
  return z.object({
    title: z.string().min(2, t('validation.titleMin')).max(200),
    slug: z
      .string()
      .min(2, t('validation.slugMin'))
      .max(200)
      .regex(SLUG_RULE, t('validation.slugPattern')),
    // In builder mode the block array carries the content, so the classic body
    // is an optional fallback (plan K-4: "Body yedek/boş").
    body: requireBody
      ? z
          .string()
          .refine(
            (v) => v.replace(/<[^>]+>/g, '').trim().length >= 20,
            t('validation.bodyMin'),
          )
      : z.string(),
    excerpt: z.string().max(500).optional().nullable(),
    pageType: z.enum(['ABOUT', 'HELP', 'ONBOARDING', 'CUSTOM']),
    coverImageUrl: z.string().max(500).optional().nullable(),
    sortOrder: z.number().int().min(0).max(9999),
    metaTitle: z.string().max(200).optional().nullable(),
    metaDescription: z.string().max(500).optional().nullable(),
    canonicalTag: z.string().max(500).optional().nullable(),
    customCode: z.string().max(5000).optional().nullable(),
    keywords: z.string().max(500, t('validation.keywordsMax')).optional().nullable(),
    allowIndexing: z.boolean(),
    changeNote: z.string().max(500).optional().nullable(),
  });
}

type Values = z.infer<ReturnType<typeof makeSchema>>;

function emptyValues(): Values {
  return {
    title: '',
    slug: '',
    body: '',
    excerpt: '',
    pageType: 'CUSTOM',
    coverImageUrl: null,
    sortOrder: 0,
    metaTitle: null,
    metaDescription: null,
    canonicalTag: null,
    customCode: null,
    keywords: null,
    allowIndexing: true,
    changeNote: null,
  };
}

function toValues(p: StaticPage): Values {
  return {
    title: p.title,
    slug: p.slug,
    body: p.body,
    excerpt: p.excerpt ?? '',
    pageType: p.pageType,
    coverImageUrl: p.coverImageUrl,
    sortOrder: p.sortOrder,
    metaTitle: p.metaTitle,
    metaDescription: p.metaDescription,
    canonicalTag: p.canonicalTag,
    customCode: p.customCode,
    keywords: p.keywords,
    allowIndexing: p.allowIndexing,
    changeNote: null,
  };
}

export function PageEditPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = !id || id === 'new';
  const [searchParams] = useSearchParams();
  // Varyant ekleme akışı: create formuna ?groupId=&lang= ile gelinir.
  const variantGroupId = searchParams.get('groupId') ?? undefined;
  const variantLang = searchParams.get('lang') ?? undefined;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const confirm = useConfirm();
  const { t } = useTranslation(['pages', 'common']);
  const statusLabel = usePageStatusLabel();
  const typeLabel = usePageTypeLabel();
  const { data: activeLanguages = [] } = useActiveLanguages();
  // Seçili içerik dili (create) — effect'siz türetme (set-state-in-effect'ten kaçınır).
  const [pickedLanguageId, setPickedLanguageId] = useState<string | undefined>(variantLang);
  const languageId = pickedLanguageId ?? pickDefaultLanguage(activeLanguages)?.id;
  const screens = Grid.useBreakpoint();
  const isMobile = !screens.md;
  const { user } = useAuth();
  // BE policy `content.publish` is admin-only in the seed. We gate publish-
  // grade workflow actions on `user.role === 'ADMIN'` per Foundation guidance.
  const isAdmin = user?.role === 'ADMIN';

  const canCreate = useCan(PERMISSIONS.PAGE_CREATE);
  const canUpdate = useCan(PERMISSIONS.PAGE_UPDATE);
  const canDelete = useCan(PERMISSIONS.PAGE_DELETE);
  // K-6 — CustomCode alanı yalnız content.customcode yetkisiyle düzenlenebilir.
  const canEditCustomCode = useCan(PERMISSIONS.CONTENT_CUSTOM_CODE);

  const [versionsOpen, setVersionsOpen] = useState(false);

  const { data: page, isLoading } = useQuery({
    queryKey: ['pages', id],
    queryFn: () => pagesApi.get(id ?? ''),
    enabled: !isNew,
  });

  // MOD-14 — page builder blocks (separate resource from the page itself).
  const { data: blocksData } = useQuery({
    queryKey: ['page-blocks', id],
    queryFn: () => pageBlocksApi.get(id ?? ''),
    enabled: !isNew,
  });
  // `useBuilder` is persisted with the blocks; the local override lets the
  // editor flip surfaces before saving (effect-free derivation).
  const [builderOverride, setBuilderOverride] = useState<boolean | undefined>(undefined);
  const useBuilder = builderOverride ?? blocksData?.useBuilder ?? false;

  // Relax the body requirement while the builder is the active content surface.
  const schema = useMemo(
    () => makeSchema(t as unknown as Translator, !useBuilder),
    [t, useBuilder],
  );

  const initialValues = useMemo<Values>(
    () => (page ? toValues(page) : emptyValues()),
    [page],
  );

  const handleMutationError = (err: unknown) => {
    if (err instanceof NormalizedApiError) {
      // K-5 — dört-göz 409'u slug çakışması DEĞİLDİR; kendi Türkçe mesajıyla
      // (errors.json) gösterilmeli, generic 409 dalına düşmemeli.
      if (
        err.code === ErrorCode.WORKFLOW_SELF_APPROVAL_FORBIDDEN ||
        err.code === ErrorCode.WORKFLOW_REVIEW_REQUIRED
      ) {
        toast.error(resolveApiErrorMessage(err));
        return;
      }
      if (
        err.status === 409 ||
        err.code === ErrorCode.SLUG_CONFLICT ||
        err.code === ErrorCode.INVALID_SLUG
      ) {
        toast.error(t('toast.slugConflict'));
        return;
      }
      if (err.code === ErrorCode.INVALID_STATE) {
        toast.error(t('toast.invalidState', { message: err.message }));
        return;
      }
    }
    toast.error(resolveApiErrorMessage(err));
  };

  const createMutation = useMutation({
    mutationFn: (input: PageWriteInput) => pagesApi.create(input),
    onSuccess: async (createdId, input) => {
      toast.success(t('toast.createdDraft'));
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.create',
        resource: 'page',
        resourceId: createdId,
        summary: t('audit.created', { title: input.title }),
      });
      void navigate(ROUTES.PAGE_EDIT.replace(':id', createdId), {
        replace: true,
      });
    },
    onError: handleMutationError,
  });

  const updateMutation = useMutation({
    mutationFn: (vars: { id: string; input: PageWriteInput }) =>
      pagesApi.update(vars.id, vars.input),
    onSuccess: async (_d, vars) => {
      toast.success(t('toast.saved'));
      await qc.invalidateQueries({ queryKey: ['pages'] });
      await qc.invalidateQueries({ queryKey: ['pages', vars.id] });
      await qc.invalidateQueries({ queryKey: ['page-versions', vars.id] });
      void recordAuditAction({
        action: 'page.update',
        resource: 'page',
        resourceId: vars.id,
        summary: t('audit.updated', { title: vars.input.title }),
      });
    },
    onError: handleMutationError,
  });

  const submitReviewMutation = useMutation({
    mutationFn: () => pagesApi.submitReview(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.submittedReview'));
      await qc.invalidateQueries({ queryKey: ['pages', id] });
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.review.submit',
        resource: 'page',
        resourceId: id,
        summary: t('audit.reviewSubmitted', {
          title: page?.title ?? t('audit.fallbackPage'),
        }),
      });
    },
    onError: handleMutationError,
  });

  const approveMutation = useMutation({
    mutationFn: () => pagesApi.approve(id ?? '', true),
    onSuccess: async () => {
      toast.success(t('toast.approved'));
      await qc.invalidateQueries({ queryKey: ['pages', id] });
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.review.approve',
        resource: 'page',
        resourceId: id,
        summary: t('audit.approved', {
          title: page?.title ?? t('audit.fallbackPage'),
        }),
      });
    },
    onError: handleMutationError,
  });

  const rejectMutation = useMutation({
    mutationFn: (note: string) => pagesApi.approve(id ?? '', false, note),
    onSuccess: async (_d, note) => {
      toast.success(t('toast.rejected'));
      await qc.invalidateQueries({ queryKey: ['pages', id] });
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.review.reject',
        resource: 'page',
        resourceId: id,
        summary: t('audit.rejected', { note }),
      });
    },
    onError: handleMutationError,
  });

  const publishMutation = useMutation({
    mutationFn: () => pagesApi.publish(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.published'));
      await qc.invalidateQueries({ queryKey: ['pages', id] });
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.publish',
        resource: 'page',
        resourceId: id,
        summary: t('audit.published', {
          title: page?.title ?? t('audit.fallbackPage'),
        }),
      });
    },
    onError: handleMutationError,
  });

  const archiveMutation = useMutation({
    mutationFn: () => pagesApi.archive(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.archived'));
      await qc.invalidateQueries({ queryKey: ['pages', id] });
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.archive',
        resource: 'page',
        resourceId: id,
        summary: t('audit.archived', {
          title: page?.title ?? t('audit.fallbackPage'),
        }),
      });
    },
    onError: handleMutationError,
  });

  const deleteMutation = useMutation({
    mutationFn: () => pagesApi.remove(id ?? ''),
    onSuccess: async () => {
      toast.success(t('toast.deletedSingle'));
      await qc.invalidateQueries({ queryKey: ['pages'] });
      void recordAuditAction({
        action: 'page.delete',
        resource: 'page',
        resourceId: id,
        summary: t('audit.deleted', {
          title: page?.title ?? t('audit.fallbackPage'),
        }),
      });
      void navigate(ROUTES.PAGES);
    },
    onError: handleMutationError,
  });

  const mutationsBusy =
    submitReviewMutation.isPending ||
    approveMutation.isPending ||
    rejectMutation.isPending ||
    publishMutation.isPending ||
    archiveMutation.isPending;

  const onSubmit = (v: Values) => {
    const payload: PageWriteInput = {
      title: v.title,
      body: v.body,
      pageType: v.pageType,
      slug: v.slug,
      excerpt: v.excerpt || null,
      coverImageUrl: v.coverImageUrl ?? null,
      sortOrder: v.sortOrder,
      metaTitle: v.metaTitle ?? null,
      metaDescription: v.metaDescription ?? null,
      canonicalTag: v.canonicalTag ?? null,
      customCode: v.customCode ?? null,
      keywords: v.keywords ?? null,
      allowIndexing: v.allowIndexing,
      changeNote: v.changeNote ?? null,
    };
    if (isNew) {
      createMutation.mutate({
        ...payload,
        languageId: languageId ?? null,
        groupId: variantGroupId ?? null,
      });
    } else {
      updateMutation.mutate({ id: id ?? '', input: payload });
    }
  };

  const currentStatus: PageStatus = page?.status ?? 'DRAFT';

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 64, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  if (!isNew && !page) {
    return (
      <div>
        <PageHeader
          title={t('edit.notFoundTitle')}
          breadcrumb={[
            { label: t('common:labels.home'), to: '/' },
            { label: t('list.breadcrumbSelf'), to: ROUTES.PAGES },
            { label: t('edit.notFoundBreadcrumb') },
          ]}
        />
        <Alert
          type="warning"
          showIcon
          message={t('edit.notFoundMessage')}
          action={
            <Button
              size="small"
              onClick={() => {
                void navigate(ROUTES.PAGES);
              }}
            >
              {t('edit.backToList')}
            </Button>
          }
        />
      </div>
    );
  }

  const canEdit = isNew ? canCreate : canUpdate;

  return (
    <div>
      <PageHeader
        title={isNew ? t('edit.newTitle') : page?.title ?? ''}
        description={
          isNew
            ? t('edit.newDescription')
            : t('edit.slugDescription', { slug: page?.slug })
        }
        breadcrumb={[
          { label: t('common:labels.home'), to: '/' },
          { label: t('list.breadcrumbSelf'), to: ROUTES.PAGES },
          { label: isNew ? t('edit.breadcrumbNew') : page?.title ?? '' },
        ]}
        actions={
          <Space wrap>
            <Button
              type="primary"
              icon={<Save size={14} />}
              htmlType="submit"
              form="page-edit-form"
              loading={createMutation.isPending || updateMutation.isPending}
              disabled={!canEdit}
            >
              {isNew ? t('edit.createDraft') : t('edit.save')}
            </Button>
          </Space>
        }
      />

      <Form
        id="page-edit-form"
        schema={schema}
        defaultValues={initialValues}
        onSubmit={onSubmit}
        options={{ values: initialValues }}
      >
        {({ watch, setValue, formState }) => {
          const titleValue = watch('title');
          const slugValue = watch('slug');
          const body = watch('body');
          const excerpt = watch('excerpt');
          const pageType = watch('pageType');
          const sortOrder = watch('sortOrder');
          const coverImageUrl = watch('coverImageUrl');
          const metaTitle = watch('metaTitle');
          const metaDescription = watch('metaDescription');
          const canonicalTag = watch('canonicalTag');
          const keywords = watch('keywords');
          const allowIndexing = watch('allowIndexing');

          const seoValue: SeoPanelValue = {
            title: metaTitle ?? '',
            description: metaDescription ?? '',
            slug: slugValue,
            ogImage: coverImageUrl ? { url: coverImageUrl } : null,
          };

          return (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: isMobile ? '1fr' : '1fr 320px',
                gap: 16,
              }}
            >
              <div
                style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
              >
                {!isNew &&
                (currentStatus === 'APPROVED' || currentStatus === 'IN_REVIEW') ? (
                  <Alert
                    type="warning"
                    showIcon
                    message={t('common:contentWorkflow.approvalInvalidationTitle')}
                    description={t('common:contentWorkflow.approvalInvalidationDescription')}
                  />
                ) : null}
                <Card title={t('edit.cards.basic')}>
                  <AntForm layout="vertical" component="div">
                    <FormField name="title" label={t('edit.fields.title')} required>
                      <Input
                        size="large"
                        maxLength={200}
                        showCount
                        placeholder={t('edit.fields.titlePlaceholder')}
                      />
                    </FormField>
                    <FormField name="slug" label={t('edit.fields.slug')} required>
                      <SlugInput
                        value={slugValue}
                        onChange={(v) =>
                          setValue('slug', v, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        sourceValue={titleValue}
                        placeholder={t('edit.fields.slugPlaceholder')}
                        prefix="/"
                      />
                    </FormField>
                    <FormField name="excerpt" label={t('edit.fields.excerpt')}>
                      <Input.TextArea
                        value={excerpt ?? ''}
                        onChange={(e) =>
                          setValue('excerpt', e.target.value, {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                        rows={2}
                        showCount
                        maxLength={500}
                        placeholder={t('edit.fields.excerptPlaceholder')}
                      />
                    </FormField>
                  </AntForm>
                </Card>

                {!useBuilder ? (
                <Card title={t('edit.cards.content')}>
                  <RichTextEditor
                    value={body}
                    onChange={(v) =>
                      setValue('body', v, {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                    }
                    placeholder={t('edit.fields.bodyPlaceholder')}
                    maxLength={20000}
                  />
                  {formState.errors.body ? (
                    <div
                      style={{
                        marginTop: 6,
                        color: 'var(--ds-danger)',
                        fontSize: 12,
                      }}
                    >
                      {formState.errors.body.message as string}
                    </div>
                  ) : null}
                </Card>
                ) : null}

                <Card title={t('edit.cards.cover')}>
                  <ImageField
                    value={coverImageUrl}
                    onChange={(url) => setValue('coverImageUrl', url, { shouldDirty: true })}
                    label={t('edit.fields.coverLabel')}
                    maxSizeMB={5}
                    hint={t('edit.fields.coverHint')}
                  />
                </Card>

                <Card>
                  <Collapse
                    defaultActiveKey={[]}
                    items={[
                      {
                        key: 'seo',
                        label: t('edit.fields.seoSettings'),
                        children: (
                          <>
                            <SeoPanel
                              value={seoValue}
                              onChange={(next) => {
                                setValue('metaTitle', next.title || null, {
                                  shouldDirty: true,
                                });
                                setValue(
                                  'metaDescription',
                                  next.description || null,
                                  { shouldDirty: true },
                                );
                                // The page entity exposes a single
                                // coverImageUrl field which doubles as the
                                // og:image source — keep them in sync so an
                                // upload from the SEO panel doesn't drop on
                                // the floor.
                                setValue(
                                  'coverImageUrl',
                                  next.ogImage?.url ?? null,
                                  { shouldDirty: true },
                                );
                              }}
                              maxTitleLength={200}
                              maxDescriptionLength={500}
                            />
                            <AntForm
                              layout="vertical"
                              component="div"
                              style={{ marginTop: 12 }}
                            >
                              <FormField
                                name="canonicalTag"
                                label={t('edit.fields.canonical')}
                              >
                                <Input
                                  placeholder={t('edit.fields.canonicalPlaceholder')}
                                  value={canonicalTag ?? ''}
                                  onChange={(e) =>
                                    setValue(
                                      'canonicalTag',
                                      e.target.value || null,
                                      { shouldDirty: true },
                                    )
                                  }
                                />
                              </FormField>
                              <AntForm.Item
                                label={t('edit.fields.keywords')}
                                help={t('edit.fields.keywordsHint')}
                              >
                                <KeywordsInput
                                  value={keywords}
                                  onChange={(v) =>
                                    setValue('keywords', v, { shouldDirty: true })
                                  }
                                  placeholder={t('edit.fields.keywordsPlaceholder')}
                                />
                              </AntForm.Item>
                              <FormField
                                name="customCode"
                                label={t('edit.fields.customCode')}
                              >
                                <CustomCodeField canEdit={canEditCustomCode} />
                              </FormField>
                              <div
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 8,
                                  marginTop: 4,
                                }}
                              >
                                <Switch
                                  checked={allowIndexing}
                                  onChange={(v) =>
                                    setValue('allowIndexing', v, {
                                      shouldDirty: true,
                                    })
                                  }
                                />
                                <span style={{ fontSize: 13 }}>
                                  {t('edit.fields.allowIndexing')}
                                </span>
                              </div>
                            </AntForm>
                          </>
                        ),
                      },
                    ]}
                  />
                </Card>
              </div>

              <aside
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 12,
                }}
              >
                {isNew ? (
                  <Card size="small" title={t('common:contentLanguage.label')}>
                    <LanguagePicker
                      value={languageId}
                      onChange={setPickedLanguageId}
                      disabled={Boolean(variantLang)}
                      style={{ width: '100%' }}
                    />
                    {variantGroupId ? (
                      <div style={{ marginTop: 8, fontSize: 12, color: 'var(--ds-text-secondary)' }}>
                        {t('variant.addingHint')}
                      </div>
                    ) : null}
                  </Card>
                ) : page ? (
                  <VariantPanel
                    currentLanguageId={page.languageId}
                    variantLanguageCodes={page.variantLanguageCodes}
                    contentType="page"
                    groupId={page.groupId}
                    onOpenVariant={(variantId) =>
                      void navigate(ROUTES.PAGE_EDIT.replace(':id', variantId))
                    }
                    onAddVariant={(lang) =>
                      void navigate(`${ROUTES.PAGE_NEW}?groupId=${page.groupId}&lang=${lang.id}`)
                    }
                  />
                ) : null}
                {!isNew && page && page.hreflangAlternates.length > 0 ? (
                  <Card size="small" title={t('edit.cards.hreflang')}>
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                      {page.hreflangAlternates.map((h) => (
                        <div
                          key={h.languageCode}
                          style={{ display: 'flex', justifyContent: 'space-between', gap: 8, fontSize: 12 }}
                        >
                          <Text code>{h.languageCode}</Text>
                          <Text type="secondary" style={{ textAlign: 'right' }}>
                            /{h.slug}
                          </Text>
                        </div>
                      ))}
                    </Space>
                  </Card>
                ) : null}
                <Card size="small" title={t('edit.cards.statusWorkflow')}>
                  {!isNew && page ? (
                    <div style={{ marginBottom: 12 }}>
                      <StatusBadge
                        kind="content"
                        status={toContentStatus(currentStatus)}
                        size="sm"
                      />
                      <span
                        style={{
                          fontSize: 12,
                          marginLeft: 8,
                          color: 'var(--ds-text-secondary)',
                        }}
                      >
                        {statusLabel(currentStatus)}
                      </span>
                    </div>
                  ) : null}
                  {isNew ? (
                    <Alert
                      type="info"
                      showIcon
                      message={t('edit.workflow.draftAlertMessage')}
                      description={t('edit.workflow.draftAlertDescription', {
                        status: statusLabel('DRAFT'),
                      })}
                    />
                  ) : (
                    <Space direction="vertical" size={8} style={{ width: '100%' }}>
                      {currentStatus === 'DRAFT' ||
                      currentStatus === 'REJECTED' ? (
                        <Button
                          block
                          type="primary"
                          loading={submitReviewMutation.isPending}
                          onClick={() => submitReviewMutation.mutate()}
                          disabled={mutationsBusy}
                        >
                          {t('edit.workflow.submitReview')}
                        </Button>
                      ) : null}

                      {currentStatus === 'IN_REVIEW' && isAdmin ? (
                        <>
                          <Button
                            block
                            type="primary"
                            loading={approveMutation.isPending}
                            onClick={() => approveMutation.mutate()}
                            disabled={mutationsBusy}
                          >
                            {t('edit.workflow.approve')}
                          </Button>
                          <Button
                            block
                            danger
                            loading={rejectMutation.isPending}
                            onClick={() => {
                              void (async () => {
                                const ok = await confirm({
                                  title: t('edit.workflow.rejectConfirmTitle'),
                                  description: t(
                                    'edit.workflow.rejectConfirmDescription',
                                  ),
                                  okText: t('edit.workflow.rejectConfirmOk'),
                                });
                                if (!ok) return;
                                const note =
                                  window.prompt(
                                    t('edit.workflow.rejectPromptTitle'),
                                    t('edit.workflow.rejectPromptDefault'),
                                  ) ?? '';
                                if (note.trim().length === 0) {
                                  toast.error(t('toast.rejectReasonRequired'));
                                  return;
                                }
                                rejectMutation.mutate(note.trim());
                              })();
                            }}
                            disabled={mutationsBusy}
                          >
                            {t('edit.workflow.reject')}
                          </Button>
                        </>
                      ) : null}

                      {(currentStatus === 'APPROVED' ||
                        currentStatus === 'DRAFT' ||
                        currentStatus === 'ARCHIVED') &&
                      isAdmin ? (
                        <Button
                          block
                          type="primary"
                          loading={publishMutation.isPending}
                          onClick={() => publishMutation.mutate()}
                          disabled={mutationsBusy}
                        >
                          {t('edit.workflow.publish')}
                        </Button>
                      ) : null}

                      {currentStatus === 'PUBLISHED' && isAdmin ? (
                        <Button
                          block
                          loading={archiveMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: t('edit.workflow.archiveConfirmTitle'),
                                description: t(
                                  'edit.workflow.archiveConfirmDescription',
                                  { title: page?.title },
                                ),
                              });
                              if (ok) archiveMutation.mutate();
                            })();
                          }}
                          disabled={mutationsBusy}
                        >
                          {t('edit.workflow.archive')}
                        </Button>
                      ) : null}

                      {!isAdmin &&
                      currentStatus !== 'DRAFT' &&
                      currentStatus !== 'REJECTED' ? (
                        <Alert
                          type="warning"
                          showIcon
                          message={t('edit.workflow.noPermission')}
                        />
                      ) : null}
                    </Space>
                  )}
                </Card>

                <Card size="small" title={t('edit.cards.typeOrder')}>
                  <Space
                    direction="vertical"
                    size={12}
                    style={{ width: '100%' }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--ds-text-secondary)',
                          marginBottom: 4,
                        }}
                      >
                        {t('edit.fields.pageType')}
                      </div>
                      <Select<PageType>
                        style={{ width: '100%' }}
                        value={pageType}
                        onChange={(v) =>
                          setValue('pageType', v, { shouldDirty: true })
                        }
                        options={PAGE_TYPES.map((pt) => ({
                          value: pt,
                          label: typeLabel(pt),
                        }))}
                      />
                    </div>
                    <div>
                      <div
                        style={{
                          fontSize: 12,
                          color: 'var(--ds-text-secondary)',
                          marginBottom: 4,
                        }}
                      >
                        {t('edit.fields.sortOrder')}
                      </div>
                      <InputNumber
                        min={0}
                        max={9999}
                        style={{ width: '100%' }}
                        value={sortOrder}
                        onChange={(v) =>
                          setValue('sortOrder', Number(v ?? 0), {
                            shouldDirty: true,
                            shouldValidate: true,
                          })
                        }
                      />
                    </div>
                  </Space>
                </Card>

                {!isNew && page ? (
                  <Card size="small" title={t('edit.cards.info')}>
                    <Space
                      direction="vertical"
                      size={6}
                      style={{ width: '100%', fontSize: 12 }}
                    >
                      <div>
                        <Text type="secondary">{t('edit.info.type')}</Text>
                        {typeLabel(page.pageType)}
                      </div>
                      {page.publishedAt ? (
                        <div>
                          <Text type="secondary">
                            {t('edit.info.publishedAt')}
                          </Text>
                          {dayjs(page.publishedAt).format('DD.MM.YYYY HH:mm')}
                        </div>
                      ) : null}
                      <div>
                        <Text type="secondary">{t('edit.info.createdAt')}</Text>
                        {dayjs(page.createdAt).format('DD.MM.YYYY HH:mm')}
                      </div>
                      <div>
                        <Text type="secondary">{t('edit.info.updatedAt')}</Text>
                        {dayjs(page.updatedAt).format('DD.MM.YYYY HH:mm')}
                      </div>
                    </Space>
                  </Card>
                ) : null}

                {!isNew && page ? (
                  <Card size="small" title={t('edit.cards.actions')}>
                    <Space
                      direction="vertical"
                      size={8}
                      style={{ width: '100%' }}
                    >
                      <Button
                        block
                        icon={<History size={14} />}
                        onClick={() => setVersionsOpen(true)}
                      >
                        {t('edit.actions.versions')}
                      </Button>
                      {canDelete || isAdmin ? (
                        <Button
                          block
                          danger
                          icon={<Trash2 size={14} />}
                          loading={deleteMutation.isPending}
                          onClick={() => {
                            void (async () => {
                              const ok = await confirm({
                                title: t('delete.modalTitle'),
                                description: t('delete.modalDescription', {
                                  title: page.title,
                                }),
                                okText: t('delete.ok'),
                              });
                              if (ok) deleteMutation.mutate();
                            })();
                          }}
                        >
                          {t('edit.actions.delete')}
                        </Button>
                      ) : null}
                      <Button
                        block
                        onClick={() => {
                          void navigate(ROUTES.PAGES);
                        }}
                      >
                        {t('edit.actions.backToList')}
                      </Button>
                    </Space>
                  </Card>
                ) : null}
              </aside>
            </div>
          );
        }}
      </Form>

      {/* Rendered OUTSIDE the page <Form>: Puck renders its own <form> for the
          fields panel, which would be invalid nested inside page-edit-form. */}
      {!isNew && id && blocksData ? (
        <div style={{ marginTop: 16 }}>
          <PageBuilderPanel
            pageId={id}
            canEdit={canEdit}
            useBuilder={useBuilder}
            onUseBuilderChange={setBuilderOverride}
            initial={blocksData}
            pageTitle={page?.title ?? ''}
          />
        </div>
      ) : null}

      {!isNew && id ? (
        <PageVersionsDrawer
          open={versionsOpen}
          pageId={id}
          currentBody={page?.body ?? ''}
          onClose={() => setVersionsOpen(false)}
        />
      ) : null}
    </div>
  );
}
